import type { components } from "../../shared/api/generated/trading-engine.js";
import type { BacktestView } from "./backtest-models.js";

export type EvidencePage = components["schemas"]["BacktestChart"];

/** Build a self-describing JSON archive from authenticated, bounded pages.
 * Never save a partial file or silently claim filtered chart decisions are complete.
 */
export async function exportBacktestEvidence(
  run: BacktestView,
  fetchPage: (cursor?: string) => Promise<EvidencePage>,
  signal: AbortSignal,
  onProgress: (pages: number) => void,
): Promise<Blob> {
  const parts: BlobPart[] = [
    JSON.stringify({
      format: "trading-engine-backtest-evidence",
      schemaVersion: 1,
      runId: run.id,
      request: run.request,
      revisionId: run.revisionId,
      summary: run.summary,
    }).slice(0, -1),
    ',"pages":[',
  ];
  let cursor: string | undefined;
  let pages = 0;
  let barCount = 0;
  let fillCount = 0;
  let first: EvidencePage | undefined;
  do {
    signal.throwIfAborted();
    const page = await fetchPage(cursor);
    signal.throwIfAborted();
    if (
      page.runId !== run.id ||
      page.symbol !== run.request.symbol ||
      page.botRunId !== run.botRunId ||
      page.decisionScope !== "all" ||
      page.range === undefined
    ) {
      throw new Error(
        "The server did not return complete evidence for this owned run. No file was saved.",
      );
    }
    if (
      first !== undefined &&
      (JSON.stringify(page.versions) !== JSON.stringify(first.versions) ||
        page.barSource !== first.barSource ||
        page.interval !== first.interval)
    ) {
      throw new Error("Evidence provenance changed between pages. No file was saved.");
    }
    first ??= page;
    const expectedFrom = cursor ?? run.request.from;
    if (
      Date.parse(page.range.from) !== Date.parse(expectedFrom) ||
      Date.parse(page.range.to) < Date.parse(page.range.from) ||
      Date.parse(page.range.to) > Date.parse(run.request.to)
    ) {
      throw new Error(
        "Evidence pages do not cover the frozen run contiguously. No file was saved.",
      );
    }
    const next = page.nextCursor;
    if (
      (next !== undefined && Date.parse(next) !== Date.parse(page.range.to) + 1) ||
      (next === undefined && Date.parse(page.range.to) !== Date.parse(run.request.to))
    ) {
      throw new Error("Evidence pagination is incomplete or does not advance. No file was saved.");
    }
    parts.push(pages === 0 ? "" : ",", JSON.stringify(page));
    barCount += page.bars.length;
    fillCount += page.fills.length;
    pages += 1;
    onProgress(pages);
    cursor = next;
  } while (cursor !== undefined);
  if (
    run.summary !== undefined &&
    (fillCount !== run.summary.fillCount ||
      (first?.barSource === "artifact" && barCount !== run.summary.barCount))
  ) {
    throw new Error(
      "Downloaded evidence counts do not match the saved summary. No file was saved.",
    );
  }
  parts.push("]}");
  return new Blob(parts, { type: "application/json" });
}
